import * as React from 'react';
import { t as translate, tryT, type MessageKey } from '@reos/i18n';
import { DomainApiError } from '../lib/api-error.js';
import { formatMoney } from '../lib/properties.js';
import {
  createReportOrder,
  listCatalog,
  lookupOrderAddress,
  type CatalogItem,
  type OrderAddressHit,
} from '../lib/orders-api.js';

/**
 * Krok NAP11 (A25) — OBJEDNAVKA REPORTU S DOPLNKY V ADMINU.
 *
 * Co bylo pred tímto krokem: doplnky (`commerce_order_items`, krok WEBF2)
 * umelo API i verejny cenik, admin je ale jen UKAZOVAL ve fronte
 * (`objednavky-fronta.tsx`) — objednat report s doplnkem slo pouze z
 * verejneho webu. Kancelar, ktera ma tarif, tedy svou vlastni objednavku
 * zalozit nemohla.
 *
 * Odkud se bere co a kam to tece:
 *  - produkty a doplnky      ← `GET /v1/public/catalog` (cenik, `scope.surcharges`),
 *  - adresa nemovitosti      ← `GET /v1/public/address-lookup` (RUIAN, kod adresniho mista),
 *  - cena                    ← SERVER (`resolveAddons` + `priceForOrder`); formular
 *                              posila jen KODY, zadne cislo (CLAUDE.md §3),
 *  - objednavka              → `POST /v1/orders` → `commerce_orders` +
 *                              `commerce_order_items` + zalohova faktura.
 */

const REPORT_KINDS = new Set(['REPORT']);

/** Nazev produktu z katalogu; neznamy klic nikdy neunikne do UI jako kod. */
function productName(item: CatalogItem): string {
  return tryT(item.nameKey as MessageKey, { locale: 'cs' }) ?? item.code;
}

/**
 * Cena, kterou kancelar za polozku plati: tarifni (`planPriceGross`), kdyz ji
 * cenik ma, jinak katalogova. Je to ZOBRAZENI — rozhodnuti o cene objednavky
 * dela server (`priceForOrder(..., scope: 'TENANT')`).
 */
function tenantPrice(item: CatalogItem): string | null {
  const amount = item.planPriceGross ?? item.priceGross;
  return formatMoney({ amount, currency: item.currency });
}

/**
 * Texty jdou pres katalog s VYSLOVNYM locale (`locale: 'cs'`) - stejne jako
 * zbytek obrazovky objednavek (`objednavky-fronta.tsx`). Hook `useT()` by tu
 * znamenal, ze karta jde vykreslit jen pod `LocaleProvider`, ktery ta stranka
 * nema.
 */
function t(key: MessageKey, values?: Record<string, string | number>): string {
  return translate(key, { locale: 'cs', ...(values === undefined ? {} : { values }) });
}

export function OrderReportCard(): React.JSX.Element {
  /**
   * Formular se rozbaluje na klik a cenik se cte TEPRVE PAK: frontu objednavek
   * otevira provozovatel kvuli kontrole reportu, ne kvuli objednavani - dotaz
   * na cenik pri kazdem otevreni te obrazovky by byl prace naprazdno.
   */
  const [open, setOpen] = React.useState(false);
  const [catalog, setCatalog] = React.useState<readonly CatalogItem[] | null>(null);
  const [productCode, setProductCode] = React.useState('');
  const [addons, setAddons] = React.useState<readonly string[]>([]);
  const [query, setQuery] = React.useState('');
  const [hits, setHits] = React.useState<readonly OrderAddressHit[] | null>(null);
  const [address, setAddress] = React.useState<OrderAddressHit | null>(null);
  const [email, setEmail] = React.useState('');
  const [billingName, setBillingName] = React.useState('');
  const [useCredit, setUseCredit] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [created, setCreated] = React.useState<string | null>(null);
  /**
   * Krok NAP12 (A28): stitek „Ukázkový výsledek…" ze SERVERU
   * (`demoSampleLabel` v odpovedi `POST /v1/orders`) — v DEMO kancelari se
   * objednavka prehraje z fixtury. Text sem posila server (zna jazyk
   * objednavky), UI ho jen vykresli; mimo demo je `null`.
   */
  const [demoLabel, setDemoLabel] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || catalog !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const items = await listCatalog();
        if (cancelled) return;
        setCatalog(items);
      } catch {
        if (!cancelled) setError(t('webAdmin.orderReport.catalogError'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, catalog]);

  const reports = (catalog ?? []).filter(
    (item) => REPORT_KINDS.has(item.kind) && item.purchasable,
  );
  const selected = reports.find((item) => item.code === productCode) ?? null;
  /*
   * Ktery doplnek je k reportu pripustny, rozhoduje KATALOG (`scope.surcharges`)
   * — ne seznam v adminu. Tentyz zdroj cte server (`allowedAddonCodesFor`),
   * takze zaskrtnuti, ktere by server odmitl, se tu ani nenabidne.
   */
  const offeredAddons = (selected?.scope?.surcharges ?? []).flatMap((code) => {
    const item = (catalog ?? []).find((row) => row.code === code);
    return item === undefined || !item.purchasable ? [] : [item];
  });

  function pickProduct(code: string): void {
    setProductCode(code);
    // Doplnky patri k reportu - po zmene produktu by zustaly nepripustne.
    setAddons([]);
    setCreated(null);
  }

  function toggleAddon(code: string): void {
    setAddons((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code],
    );
  }

  async function search(): Promise<void> {
    setError(null);
    setHits(null);
    try {
      setHits(await lookupOrderAddress(query));
    } catch {
      setError(t('webAdmin.orderReport.addressError'));
    }
  }

  const ready =
    selected !== null &&
    address?.ruianAddressCode !== undefined &&
    address?.ruianAddressCode !== null &&
    email.trim() !== '' &&
    billingName.trim().length >= 2;

  async function submit(): Promise<void> {
    if (!ready || selected === null || address?.ruianAddressCode == null) return;
    setBusy(true);
    setError(null);
    setCreated(null);
    setDemoLabel(null);
    try {
      const order = await createReportOrder({
        productCode: selected.code,
        ruianAddressCode: address.ruianAddressCode,
        email: email.trim(),
        billingName: billingName.trim(),
        addons,
        useCredit,
      });
      setCreated(order.orderNumber);
      setDemoLabel(order.demoSampleLabel ?? null);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.orderReport.submitError'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-card mb-4" style={{ padding: 16 }} data-testid="order-report-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.orderReport.title')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.orderReport.perex')}</p>

      <button
        type="button"
        className="rh-btn mb-3"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        data-testid="order-report-toggle"
      >
        {open ? t('webAdmin.orderReport.closeForm') : t('webAdmin.orderReport.openForm')}
      </button>

      {!open ? null : (
        <>
          <label className="mb-1 block text-sm font-medium" htmlFor="order-report-product">
            {t('webAdmin.orderReport.product')}
          </label>
          <select
            id="order-report-product"
            className="rh-input mb-3 w-full"
            value={productCode}
            onChange={(e) => pickProduct(e.target.value)}
            data-testid="order-report-product"
          >
            <option value="">{t('webAdmin.orderReport.productPlaceholder')}</option>
            {reports.map((item) => (
              <option key={item.code} value={item.code}>
                {`${productName(item)} — ${tenantPrice(item) ?? ''}`}
              </option>
            ))}
          </select>

          {offeredAddons.length === 0 ? null : (
            <fieldset className="mb-3" data-testid="order-report-addons">
              <legend className="mb-1 text-sm font-medium">{t('webAdmin.orderReport.addons')}</legend>
              {offeredAddons.map((item) => (
                <label key={item.code} className="mb-1 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={addons.includes(item.code)}
                    onChange={() => toggleAddon(item.code)}
                    data-testid={`order-report-addon-${item.code}`}
                  />
                  <span>{`${productName(item)} — ${tenantPrice(item) ?? ''}`}</span>
                </label>
              ))}
              <p className="text-xs text-gray-500">{t('webAdmin.orderReport.addonsNote')}</p>
            </fieldset>
          )}

          <label className="mb-1 block text-sm font-medium" htmlFor="order-report-address">
            {t('webAdmin.orderReport.address')}
          </label>
          <div className="mb-2 flex flex-wrap gap-2">
            <input
              id="order-report-address"
              className="rh-input min-w-0 flex-1"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('webAdmin.orderReport.addressPlaceholder')}
              data-testid="order-report-address"
            />
            <button
              type="button"
              className="rh-btn"
              onClick={() => void search()}
              disabled={query.trim().length < 3}
              data-testid="order-report-address-search"
            >
              {t('webAdmin.orderReport.addressSearch')}
            </button>
          </div>
          {hits === null ? null : hits.length === 0 ? (
            <p className="mb-2 text-sm text-gray-600" data-testid="order-report-address-empty">
              {t('webAdmin.orderReport.addressEmpty')}
            </p>
          ) : (
            <ul className="mb-2 list-none p-0" data-testid="order-report-address-hits">
              {hits.map((hit) => (
                <li key={hit.ruianAddressCode ?? hit.formatted} className="mb-1">
                  <button
                    type="button"
                    className="rh-btn rh-btn--ghost text-left text-sm"
                    onClick={() => setAddress(hit)}
                    data-testid={`order-report-address-pick-${hit.ruianAddressCode ?? 'x'}`}
                  >
                    {hit.formatted}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {address === null ? null : (
            <p className="mb-3 text-sm" data-testid="order-report-address-selected">
              {address.formatted}
            </p>
          )}

          <label className="mb-1 block text-sm font-medium" htmlFor="order-report-email">
            {t('webAdmin.orderReport.email')}
          </label>
          <input
            id="order-report-email"
            type="email"
            className="rh-input mb-3 w-full"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="order-report-email"
          />

          <label className="mb-1 block text-sm font-medium" htmlFor="order-report-billing">
            {t('webAdmin.orderReport.billingName')}
          </label>
          <input
            id="order-report-billing"
            className="rh-input mb-3 w-full"
            value={billingName}
            onChange={(e) => setBillingName(e.target.value)}
            data-testid="order-report-billing"
          />

          <label className="mb-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={useCredit}
              onChange={(e) => setUseCredit(e.target.checked)}
              data-testid="order-report-use-credit"
            />
            <span>{t('webAdmin.orderReport.useCredit')}</span>
          </label>

          <button
            type="button"
            className="rh-btn rh-btn--primary"
            disabled={!ready || busy}
            onClick={() => void submit()}
            data-testid="order-report-submit"
          >
            {t('webAdmin.orderReport.submit')}
          </button>

          {error === null ? null : (
            <p className="mt-2 text-sm text-red-700" role="alert" data-testid="order-report-error">
              {error}
            </p>
          )}
          {created === null ? null : (
            <p className="mt-2 text-sm" data-testid="order-report-created">
              {t('webAdmin.orderReport.created', { orderNumber: created })}
            </p>
          )}
          {demoLabel === null ? null : (
            <p className="mt-1 text-sm text-gray-600" data-testid="order-report-demo-sample">
              {demoLabel}
            </p>
          )}
        </>
      )}
    </div>
  );
}
